import React, { useEffect, useRef, useState } from "react";
import { AdminLayout } from "@/components/DashboardLayout";
import { api, API } from "@/lib/api";
import * as Lucide from "lucide-react";
import { toast } from "sonner";
import PaymentBadges from "@/components/PaymentBadges";

const ALL_BRANDS = ["visa", "mastercard", "amex", "rupay", "upi", "wallet", "netbanking", "paypal"];

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [uploadingBrand, setUploadingBrand] = useState(null);
  const fileInputs = useRef({});
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <AdminLayout><div className="text-slate-400">Loading…</div></AdminLayout>;

  const update = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
  const toggleBrand = (b) => {
    const cur = new Set(s.footer_accepted_brands || []);
    if (cur.has(b)) cur.delete(b); else cur.add(b);
    update("footer_accepted_brands", Array.from(cur));
  };
  const save = async () => {
    try {
      await api.put("/admin/settings", {
        brand: s.brand,
        company: s.company,
        tagline: s.tagline,
        email: s.email,
        support_email: s.support_email,
        low_credit_threshold: Number(s.low_credit_threshold || 0),
        footer_show_accepted_payments: !!s.footer_show_accepted_payments,
        footer_accepted_brands: s.footer_accepted_brands || [],
        footer_payment_processor_text: s.footer_payment_processor_text || "",
        footer_brand_logos: s.footer_brand_logos || {},
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setBrandLogo = (b, url) => {
    setS((prev) => {
      const logos = { ...(prev.footer_brand_logos || {}) };
      if (url) logos[b] = url; else delete logos[b];
      return { ...prev, footer_brand_logos: logos };
    });
  };

  const uploadBrandLogo = async (brand, file) => {
    if (!file) return;
    if (file.size > 1_000_000) { toast.error("Max 1 MB"); return; }
    setUploadingBrand(brand);
    try {
      const fd = new FormData();
      fd.append("brand", brand);
      fd.append("file", file);
      const r = await api.post("/admin/uploads/brand-logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const absUrl = `${API}${r.data.url}`;
      setBrandLogo(brand, absUrl);
      // Persist immediately so the upload isn't lost on page reload
      await api.put("/admin/settings", {
        footer_brand_logos: {
          ...(s.footer_brand_logos || {}),
          [brand]: absUrl,
        },
      });
      toast.success(`${brand} logo uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingBrand(null);
    }
  };

  return (
    <AdminLayout>
      <h1 className="bx-heading text-3xl font-extrabold">Website Settings</h1>
      <p className="text-slate-400 text-sm mt-1">Branding, contact emails, and the public footer.</p>

      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <div className="bx-card p-5">
          <div className="bx-heading text-lg font-semibold">Brand</div>
          <div className="mt-3 grid grid-cols-1 gap-3">
            <input data-testid="settings-brand" value={s.brand || ""} onChange={(e) => update("brand", e.target.value)} placeholder="Brand" className="bx-input" />
            <input value={s.company || ""} onChange={(e) => update("company", e.target.value)} placeholder="Company" className="bx-input" />
            <input value={s.tagline || ""} onChange={(e) => update("tagline", e.target.value)} placeholder="Tagline" className="bx-input" />
            <input value={s.email || ""} onChange={(e) => update("email", e.target.value)} placeholder="Public email" className="bx-input" />
            <input value={s.support_email || ""} onChange={(e) => update("support_email", e.target.value)} placeholder="Support email" className="bx-input" />
            <div>
              <label className="text-xs text-slate-400">Low-credit warning threshold</label>
              <input type="number" value={s.low_credit_threshold || 0} onChange={(e) => update("low_credit_threshold", e.target.value)} className="bx-input mt-1" />
            </div>
          </div>
        </div>

        <div className="bx-card p-5">
          <div className="bx-heading text-lg font-semibold">Footer — Accepted Payments</div>
          <label className="flex items-center gap-2 text-sm mt-3">
            <input data-testid="settings-payments-toggle" type="checkbox" checked={!!s.footer_show_accepted_payments} onChange={(e) => update("footer_show_accepted_payments", e.target.checked)} />
            Show "We Accept" badges in the website footer
          </label>
          <div className="mt-3">
            <label className="text-xs text-slate-400">Payment Processor text (optional)</label>
            <input
              data-testid="settings-processor-text"
              value={s.footer_payment_processor_text || ""}
              onChange={(e) => update("footer_payment_processor_text", e.target.value)}
              placeholder="e.g. Payments processed securely via Razorpay & Stripe"
              className="bx-input mt-1"
            />
          </div>
          <div className="mt-4">
            <div className="text-xs text-slate-400 mb-2">Brands to display</div>
            <div className="flex flex-wrap gap-2">
              {ALL_BRANDS.map((b) => {
                const active = (s.footer_accepted_brands || []).includes(b);
                return (
                  <button
                    key={b}
                    data-testid={`settings-brand-${b}`}
                    onClick={() => toggleBrand(b)}
                    className={`px-3 py-1.5 rounded-full text-xs capitalize ${active ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white" : "bg-white/5 border border-white/10 text-slate-300"}`}
                  >
                    {b}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom brand logo URLs */}
          <div className="mt-5">
            <div className="text-xs text-slate-400 mb-2">Custom logo (paste a URL or upload an image — overrides the built-in badge)</div>
            <div className="space-y-2 max-h-60 overflow-auto bx-scrollbar pr-1">
              {(s.footer_accepted_brands || []).map((b) => (
                <div key={b} className="flex items-center gap-2">
                  <span className="text-xs text-slate-300 capitalize w-24 shrink-0">{b}</span>
                  <input
                    data-testid={`settings-brand-logo-${b}`}
                    value={(s.footer_brand_logos || {})[b] || ""}
                    onChange={(e) => setBrandLogo(b, e.target.value)}
                    placeholder="https://… or upload below"
                    className="bx-input bx-mono text-xs py-1.5 flex-1"
                  />
                  <input
                    ref={(el) => { fileInputs.current[b] = el; }}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                    style={{ display: "none" }}
                    data-testid={`settings-brand-logo-file-${b}`}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadBrandLogo(b, f);
                      e.target.value = "";
                    }}
                  />
                  <button
                    type="button"
                    data-testid={`settings-brand-logo-upload-${b}`}
                    onClick={() => fileInputs.current[b]?.click()}
                    disabled={uploadingBrand === b}
                    className="px-2 py-1.5 rounded text-xs bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10 disabled:opacity-50 shrink-0"
                    title="Upload custom logo"
                  >
                    {uploadingBrand === b ? (
                      <Lucide.Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Lucide.Upload className="w-3.5 h-3.5" />
                    )}
                  </button>
                  {(s.footer_brand_logos || {})[b] && (
                    <>
                      <img src={(s.footer_brand_logos || {})[b]} alt={b} style={{ height: 22, background: "#fff", padding: "2px 4px", borderRadius: 4 }} />
                      <button
                        type="button"
                        data-testid={`settings-brand-logo-clear-${b}`}
                        onClick={() => setBrandLogo(b, "")}
                        className="text-slate-400 hover:text-rose-400 shrink-0"
                        title="Remove logo"
                      >
                        <Lucide.X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              ))}
              {!(s.footer_accepted_brands || []).length && (
                <div className="text-xs text-slate-500">Toggle on at least one brand above to add a logo URL.</div>
              )}
            </div>
          </div>
          <div className="mt-5 p-3 rounded-lg border border-white/10 bg-black/40">
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-2">Preview</div>
            {s.footer_show_accepted_payments ? (
              <PaymentBadges
                brands={s.footer_accepted_brands || []}
                processorText={s.footer_payment_processor_text || ""}
                logos={s.footer_brand_logos || {}}
              />
            ) : (
              <div className="text-xs text-slate-500">Toggle on to preview.</div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <button data-testid="settings-save" onClick={save} className="bx-btn-primary"><Lucide.Save size={16} /> Save Settings</button>
      </div>
    </AdminLayout>
  );
}
